// Commitment create modal — FE-043c.
//
// Two-stage flow rendered from a single component so the title
// carries through without remounting form state:
//
//   Stage 1 — quick create
//     Single text input for title + Continue / Cancel.
//     Submitting promotes the modal stage to "full-create" with
//     the title preserved.
//   Stage 2 — full create
//     The full Commitment form rendered inside the same modal,
//     using SearchableSelect pickers (Account, Assignee, Reviewer,
//     Tags) so the broken "type a UUID into a text input" UX is
//     gone for good.
//
// Why a single component? React Query and the form's controlled
// inputs need to keep their state across the stage transition;
// hoisting both stages into one tree means the title typed in
// Stage 1 is already in ``values.title`` when Stage 2 mounts, no
// prop drilling.

import { useEffect, useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";

import { Modal } from "@/design-system/components/Modal";

import {
  blankCommitmentFormValues,
  serializeCommitmentForm,
  validateCommitmentForm,
  type CommitmentFormErrors,
} from "./commitmentFormValidation";
import {
  useCommitmentModal,
  type CommitmentModalDefaults,
} from "./CommitmentModalProvider";
import { CommitmentModalForm } from "./CommitmentModalForm";
import { useCreateCommitmentMutation } from "./hooks";
import type {
  CommitmentFormValues,
  CommitmentMutationResponse,
} from "./types";


function valuesFromDefaults(
  defaults: CommitmentModalDefaults,
): CommitmentFormValues {
  const blank = blankCommitmentFormValues();
  return {
    ...blank,
    title: defaults.title ?? blank.title,
    account_id: defaults.account_id ?? blank.account_id,
    plan_year_id: defaults.plan_year_id ?? blank.plan_year_id,
    category_slug: defaults.category_slug ?? blank.category_slug,
    due_date_time: defaults.due_date_time ?? blank.due_date_time,
    source_touchpoint_id:
      defaults.source_touchpoint_id ?? blank.source_touchpoint_id,
  };
}


export function CommitmentCreateModal() {
  const modal = useCommitmentModal();
  const navigate = useNavigate();
  const createMutation = useCreateCommitmentMutation();
  const open =
    modal.state.stage === "quick-create"
      || modal.state.stage === "full-create";
  const stage = modal.state.stage;

  // The form values live here so the title typed in stage 1 is
  // already populated when the user lands on stage 2. ``valuesKey``
  // doubles as the "session" id for the modal — we reset values
  // whenever a fresh openCreateCommitmentModal call fires (defaults
  // change), but *not* when the same call promotes between stages.
  const initialValues = useMemo(
    () => valuesFromDefaults(modal.state.defaults),
    [modal.state.defaults],
  );

  const [values, setValues] = useState<CommitmentFormValues>(initialValues);
  const [errors, setErrors] = useState<CommitmentFormErrors>({});
  const [serverError, setServerError] = useState<string | null>(null);
  // Track which set of defaults we initialised against. When the
  // caller opens a fresh create flow with different defaults, reset
  // values; promotions inside the same flow keep them.
  const seededAgainstRef = useRef<CommitmentModalDefaults | null>(null);

  useEffect(() => {
    if (!open) {
      seededAgainstRef.current = null;
      setValues(initialValues);
      setErrors({});
      setServerError(null);
      return;
    }
    if (seededAgainstRef.current !== modal.state.defaults) {
      seededAgainstRef.current = modal.state.defaults;
      setValues(initialValues);
      setErrors({});
      setServerError(null);
    }
  }, [open, modal.state.defaults, initialValues]);

  function handleChange<K extends keyof CommitmentFormValues>(
    key: K, value: CommitmentFormValues[K],
  ): void {
    setValues((prev) => ({ ...prev, [key]: value }));
    if (errors[key]) {
      setErrors((prev) => {
        const next = { ...prev };
        delete next[key];
        return next;
      });
    }
  }

  function handleQuickContinue(): void {
    const trimmed = values.title.trim();
    if (trimmed.length === 0) {
      setErrors((prev) => ({ ...prev, title: "Title is required." }));
      return;
    }
    setErrors({});
    modal.promoteToFullCreate(trimmed);
  }

  function handleFullSubmit(): void {
    const { errors: validationErrors, ok } = validateCommitmentForm(values);
    if (!ok) {
      setErrors(validationErrors);
      return;
    }
    setErrors({});
    setServerError(null);
    const payload = serializeCommitmentForm(values);
    createMutation.mutate(payload, {
      onSuccess: (data: CommitmentMutationResponse) => {
        modal.closeCommitmentModal();
        // Route to the canonical detail page once the modal is
        // dismissed. The view-modal stage is reserved for the
        // follow-up — until it lands, the detail page stays the
        // canonical "view" surface.
        navigate(`/dashboard/commitments/${data.commitment_id}`);
      },
      onError: (err: Error) => {
        setServerError(
          err.message
            ? `Couldn't create this Commitment: ${err.message}`
            : "Couldn't create this Commitment. Refresh and try again.",
        );
      },
    });
  }

  if (!open) return null;

  if (stage === "quick-create") {
    return (
      <Modal
        open
        onClose={modal.closeCommitmentModal}
        title="New Commitment"
        size="sm"
        footer={
          <>
            <button
              type="button"
              data-testid="commitment-quick-create-cancel"
              onClick={modal.closeCommitmentModal}
              className="rounded-md border border-border-subtle px-3 py-1.5 text-sm text-text-primary hover:bg-recessed"
            >
              Cancel
            </button>
            <button
              type="button"
              data-testid="commitment-quick-create-continue"
              onClick={handleQuickContinue}
              className="rounded-md bg-teal px-3 py-1.5 text-sm font-medium text-text-inverse hover:opacity-90"
            >
              Continue
            </button>
          </>
        }
      >
        <form
          data-testid="commitment-quick-create-form"
          onSubmit={(e) => {
            e.preventDefault();
            handleQuickContinue();
          }}
          className="flex flex-col gap-2"
        >
          <label
            htmlFor="commitment-quick-create-title"
            className="text-xs font-medium text-text-secondary"
          >
            Title
            <span className="ml-0.5 text-ember-ink">*</span>
          </label>
          <input
            id="commitment-quick-create-title"
            data-testid="commitment-quick-create-title"
            type="text"
            autoFocus
            value={values.title}
            onChange={(e) => handleChange("title", e.target.value)}
            placeholder="What needs to happen?"
            className="rounded-md border border-border-subtle bg-canvas/40 px-3 py-2 text-sm text-text-primary"
          />
          {errors.title && (
            <p
              role="alert"
              data-testid="commitment-quick-create-title-error"
              className="text-[11px] font-medium text-ember-ink"
            >
              {errors.title}
            </p>
          )}
          <p className="text-[11px] text-text-tertiary">
            Press Continue to set Account, due date, and other fields.
          </p>
        </form>
      </Modal>
    );
  }

  // Stage 2 — full form.
  return (
    <Modal
      open
      onClose={modal.closeCommitmentModal}
      title="New Commitment"
      size="lg"
      footer={
        <>
          <button
            type="button"
            data-testid="commitment-full-create-cancel"
            onClick={modal.closeCommitmentModal}
            className="rounded-md border border-border-subtle px-3 py-1.5 text-sm text-text-primary hover:bg-recessed"
          >
            Cancel
          </button>
          <button
            type="button"
            data-testid="commitment-full-create-submit"
            onClick={handleFullSubmit}
            disabled={createMutation.isPending}
            className="rounded-md bg-teal px-3 py-1.5 text-sm font-medium text-text-inverse hover:opacity-90 disabled:cursor-not-allowed disabled:opacity-60"
          >
            {createMutation.isPending ? "Creating…" : "Create Commitment"}
          </button>
        </>
      }
    >
      <div
        // Quick → full promotion swaps a tiny single-field body for
        // the full form. Fade the new body in so it lands gently
        // alongside the dialog's max-width transition (otherwise the
        // larger surface pops in before the eye can track it).
        className="animate-fade-in"
      >
        <CommitmentModalForm
          values={values}
          errors={errors}
          onChange={handleChange}
          serverError={serverError}
        />
      </div>
    </Modal>
  );
}
